import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { addressBookApi, type AddressEntry, type AddressEntryInput } from './api.ts';
import { dv } from '../../i18n/dv.ts';

type FormState = {
  officeName: string; addresseePerson: string; address: string;
  atoll: string; island: string; phone: string; email: string;
};

const EMPTY_FORM: FormState = {
  officeName: '', addresseePerson: '', address: '', atoll: '', island: '', phone: '', email: '',
};

function toFormState(e: AddressEntry): FormState {
  return {
    officeName: e.officeName,
    addresseePerson: e.addresseePerson ?? '',
    address: e.address ?? '',
    atoll: e.atoll ?? '',
    island: e.island ?? '',
    phone: e.phone ?? '',
    email: e.email ?? '',
  };
}

// Trim every field, then drop empties so optional columns clear to null
// instead of being sent as ''.
function toPayload(f: FormState): Partial<AddressEntryInput> & { officeName: string } {
  const trimmed = Object.fromEntries(
    Object.entries(f).map(([k, v]) => [k, v.trim()]),
  ) as FormState;
  const payload: Record<string, string> = {};
  for (const [k, v] of Object.entries(trimmed)) {
    if (v) payload[k] = v;
  }
  return { ...payload, officeName: trimmed.officeName } as Partial<AddressEntryInput> & { officeName: string };
}

function EntryForm({
  initial, onCancel, onSave, saving,
}: { initial: FormState; onCancel: () => void; onSave: (f: FormState) => void; saving: boolean }): JSX.Element {
  const [form, setForm] = useState<FormState>(initial);
  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => { e.preventDefault(); if (form.officeName.trim()) onSave(form); }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-sm">
          <span className="field-label">{dv.addressBook.officeName} *</span>
          <input className="input w-full" value={form.officeName} onChange={set('officeName')} maxLength={200} required />
        </label>
        <label className="space-y-1 text-sm">
          <span className="field-label">{dv.addressBook.addresseePerson}</span>
          <input className="input w-full" value={form.addresseePerson} onChange={set('addresseePerson')} maxLength={200} />
        </label>
        <label className="space-y-1 text-sm sm:col-span-2">
          <span className="field-label">{dv.addressBook.address}</span>
          <input className="input w-full" value={form.address} onChange={set('address')} maxLength={200} />
        </label>
        <label className="space-y-1 text-sm">
          <span className="field-label">{dv.addressBook.atoll}</span>
          <input className="input w-full" value={form.atoll} onChange={set('atoll')} maxLength={200} />
        </label>
        <label className="space-y-1 text-sm">
          <span className="field-label">{dv.addressBook.island}</span>
          <input className="input w-full" value={form.island} onChange={set('island')} maxLength={200} />
        </label>
        <label className="space-y-1 text-sm">
          <span className="field-label">{dv.addressBook.phone}</span>
          <input className="input w-full" value={form.phone} onChange={set('phone')} maxLength={200} dir="ltr" />
        </label>
        <label className="space-y-1 text-sm">
          <span className="field-label">{dv.addressBook.email}</span>
          <input className="input w-full" type="email" value={form.email} onChange={set('email')} maxLength={200} dir="ltr" />
        </label>
      </div>
      <div className="flex items-center gap-2">
        <button type="submit" disabled={saving || !form.officeName.trim()} className="btn-primary btn-sm">
          {dv.common.save}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary btn-sm">
          {dv.common.cancel}
        </button>
      </div>
    </form>
  );
}

function EntryRow({ entry }: { entry: AddressEntry }): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['addressBook'] });

  const update = useMutation({
    mutationFn: (patch: Partial<AddressEntryInput>) => addressBookApi.update(entry.id, patch),
    onSuccess: () => { void invalidate(); setEditing(false); },
  });
  const remove = useMutation({
    mutationFn: () => addressBookApi.remove(entry.id),
    onSuccess: () => { void invalidate(); },
  });

  if (editing) {
    return (
      <div className="p-4">
        <EntryForm
          initial={toFormState(entry)}
          saving={update.isPending}
          onCancel={() => setEditing(false)}
          onSave={(f) => update.mutate(toPayload(f))}
        />
        {update.isError && <p role="alert" className="mt-3 text-sm text-destructive">{dv.common.error}</p>}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-start justify-between gap-3.5 p-5 transition-colors hover:bg-[hsl(var(--surface-high))]">
      <div className="min-w-0 flex-1">
        <p className="font-medium leading-tight">{entry.officeName}</p>
        {entry.addresseePerson && <p className="mt-1 text-sm text-muted-foreground">{entry.addresseePerson}</p>}
        {entry.address && <p className="mt-0.5 text-sm text-muted-foreground">{entry.address}</p>}
        <p className="mt-0.5 text-sm text-muted-foreground">
          {[entry.island, entry.atoll].filter(Boolean).join('، ')}
        </p>
        {(entry.phone || entry.email) && (
          <p className="mono-meta mt-1 text-muted-foreground" dir="ltr">
            {[entry.phone, entry.email].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button type="button" onClick={() => setEditing(true)} className="btn-secondary btn-sm">
          {dv.common.edit}
        </button>
        <button
          type="button"
          disabled={remove.isPending}
          onClick={() => { if (window.confirm(dv.addressBook.deleteConfirm)) remove.mutate(); }}
          className="btn-secondary btn-sm text-destructive"
        >
          {dv.common.delete}
        </button>
      </div>
      {remove.isError && <p role="alert" className="w-full text-sm text-destructive">{dv.common.error}</p>}
    </div>
  );
}

export function AddressBookSettings(): JSX.Element {
  const { data: entries, isLoading, isError } = useQuery({ queryKey: ['addressBook'], queryFn: addressBookApi.list });
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const list = entries ?? [];

  const create = useMutation({
    mutationFn: (payload: Partial<AddressEntryInput> & { officeName: string }) => addressBookApi.create(payload),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['addressBook'] }); setAdding(false); },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="section-heading text-xl font-semibold">
          {dv.addressBook.title}
          {list.length > 0 && <span className="pill pill-active shrink-0">{list.length}</span>}
        </h2>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className="btn-primary btn-sm">
            {dv.addressBook.add}
          </button>
        )}
      </div>

      {adding && (
        <section className="card-paper p-5 sm:p-6">
          <EntryForm
            initial={EMPTY_FORM}
            saving={create.isPending}
            onCancel={() => setAdding(false)}
            onSave={(f) => create.mutate(toPayload(f))}
          />
          {create.isError && <p role="alert" className="mt-3 text-sm text-destructive">{dv.common.error}</p>}
        </section>
      )}

      <section className="card-paper divide-y divide-border overflow-hidden p-0">
        {list.map((e) => <EntryRow key={e.id} entry={e} />)}
        {isError && <p role="alert" className="p-8 text-center text-sm text-destructive">{dv.common.error}</p>}
        {!isError && list.length === 0 && (
          <p className="p-8 text-center text-sm text-muted-foreground">
            {isLoading ? dv.common.loading : dv.addressBook.empty}
          </p>
        )}
      </section>
    </div>
  );
}
